import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  FileCheck, PlusCircle, X, CheckCircle, Clock, XCircle,
  ShieldCheck, RefreshCw, ChevronDown, ChevronUp, Copy
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CFG = {
  pending:    { label: "Pending",    color: "text-[#FF9F0A]", bg: "bg-[#FF9F0A]/10", border: "border-[#FF9F0A]/20", Icon: Clock },
  notarized:  { label: "Notarized",  color: "text-[#00FF88]", bg: "bg-emerald-500/10", border: "border-emerald-500/20", Icon: CheckCircle },
  rejected:   { label: "Rejected",   color: "text-[#FF3B30]", bg: "bg-red-500/10", border: "border-red-500/20", Icon: XCircle },
};

function relTime(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(iso).toLocaleDateString();
}

// ── Request Modal ──────────────────────────────────────────────────────────────
function RequestModal({ dealId, onClose, onCreated }) {
  const [docName, setDocName] = useState("");
  const [notes, setNotes] = useState("");
  const [parties, setParties] = useState([{ name: "", role: "buyer" }]);
  const [saving, setSaving] = useState(false);

  const addParty = () => setParties(p => [...p, { name: "", role: "party" }]);
  const removeParty = i => setParties(p => p.filter((_, idx) => idx !== i));
  const updateParty = (i, field, val) =>
    setParties(p => p.map((party, idx) => idx === i ? { ...party, [field]: val } : party));

  const submit = async () => {
    if (!docName.trim()) { toast.error("Document name required"); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/notarize`, {
        deal_id: dealId,
        document_name: docName.trim(),
        notes: notes.trim(),
        parties: parties.filter(p => p.name.trim()),
      }, { withCredentials: true });
      toast.success("Notarization request submitted");
      onCreated(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit request");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#0A0A0A] border border-white/[0.08] rounded-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-sm font-semibold text-white">Request Notarization</span>
          </div>
          <button onClick={onClose} className="text-white/30 hover:text-white/70 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-[10px] text-white/40 uppercase tracking-wider block mb-1.5">Document Name *</label>
            <input
              value={docName}
              onChange={e => setDocName(e.target.value)}
              placeholder="e.g. Purchase Agreement, Deed of Trust..."
              className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-[#D4AF37]/40"
              data-testid="notarize-doc-name-input"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[10px] text-white/40 uppercase tracking-wider">Parties</label>
              <button onClick={addParty} className="text-[10px] text-[#D4AF37] hover:text-[#D4AF37]/70 flex items-center gap-1">
                <PlusCircle className="w-3 h-3" /> Add party
              </button>
            </div>
            <div className="space-y-2">
              {parties.map((p, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <input
                    value={p.name}
                    onChange={e => updateParty(i, "name", e.target.value)}
                    placeholder="Full name"
                    className="flex-1 bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-[#D4AF37]/40"
                  />
                  <select
                    value={p.role}
                    onChange={e => updateParty(i, "role", e.target.value)}
                    className="bg-white/[0.04] border border-white/[0.08] rounded-lg px-2 py-2 text-sm text-white/70 focus:outline-none"
                  >
                    {["buyer","seller","lender","investor","contractor","realtor","title","witness"].map(r => (
                      <option key={r} value={r} className="bg-[#0A0A0A]">{r.charAt(0).toUpperCase() + r.slice(1)}</option>
                    ))}
                  </select>
                  {parties.length > 1 && (
                    <button onClick={() => removeParty(i)} className="text-white/20 hover:text-[#FF3B30] transition-colors">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[10px] text-white/40 uppercase tracking-wider block mb-1.5">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Any special instructions or context..."
              rows={2}
              className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-[#D4AF37]/40 resize-none"
            />
          </div>

          <button
            onClick={submit}
            disabled={saving}
            className="w-full py-2.5 bg-[#D4AF37] text-black text-sm font-bold uppercase tracking-wider rounded-lg hover:bg-[#D4AF37]/90 transition-all disabled:opacity-50"
            data-testid="notarize-submit-btn"
          >
            {saving ? "Submitting..." : "Request Notarization"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Complete Modal ─────────────────────────────────────────────────────────────
function CompleteModal({ notarization, onClose, onCompleted }) {
  const [notaryName, setNotaryName] = useState("");
  const [statement, setStatement] = useState(
    `I do hereby certify that on this date I have witnessed the execution of the document '${notarization.document_name}' and attest to its authenticity.`
  );
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!notaryName.trim()) { toast.error("Your name is required to notarize"); return; }
    setSaving(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/notarize/${notarization.notarization_id}/complete`,
        { notary_name: notaryName.trim(), notary_statement: statement.trim() },
        { withCredentials: true }
      );
      toast.success(`Notarized! Certificate: ${res.data.certificate_id}`);
      onCompleted(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to notarize");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#0A0A0A] border border-white/[0.08] rounded-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-[#00FF88]" />
            <span className="text-sm font-semibold text-white">Complete Notarization</span>
          </div>
          <button onClick={onClose} className="text-white/30 hover:text-white/70 transition-colors"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="p-3 bg-white/[0.02] border border-white/[0.05] rounded-xl">
            <p className="text-[10px] text-white/30 uppercase tracking-wider mb-0.5">Document</p>
            <p className="text-white text-sm font-medium">{notarization.document_name}</p>
            {notarization.property_address && (
              <p className="text-white/40 text-xs mt-0.5">{notarization.property_address}</p>
            )}
          </div>

          <div>
            <label className="text-[10px] text-white/40 uppercase tracking-wider block mb-1.5">Your Full Name (as Notary) *</label>
            <input
              value={notaryName}
              onChange={e => setNotaryName(e.target.value)}
              placeholder="Enter your full legal name"
              className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-[#00FF88]/40"
              data-testid="notarize-notary-name-input"
            />
          </div>

          <div>
            <label className="text-[10px] text-white/40 uppercase tracking-wider block mb-1.5">Notary Statement</label>
            <textarea
              value={statement}
              onChange={e => setStatement(e.target.value)}
              rows={4}
              className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2.5 text-xs text-white/70 placeholder:text-white/20 focus:outline-none focus:border-[#00FF88]/40 resize-none"
            />
          </div>

          <div className="p-3 bg-amber-500/5 border border-amber-500/20 rounded-xl">
            <p className="text-[10px] text-amber-400/70 leading-relaxed">
              By clicking Notarize, you certify that you have verified the identity of all parties and witnessed the execution of this document. This action is recorded with a timestamp and your IP address for audit purposes.
            </p>
          </div>

          <button
            onClick={submit}
            disabled={saving}
            className="w-full py-2.5 bg-emerald-600 text-white text-sm font-bold uppercase tracking-wider rounded-lg hover:bg-emerald-500 transition-all disabled:opacity-50"
            data-testid="notarize-confirm-btn"
          >
            {saving ? "Notarizing..." : "Notarize Document"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Certificate Card ───────────────────────────────────────────────────────────
function CertificateCard({ n }) {
  const [expanded, setExpanded] = useState(false);
  const cfg = STATUS_CFG[n.status] || STATUS_CFG.pending;
  const Icon = cfg.Icon;

  return (
    <div className="bg-white/[0.02] border border-white/[0.05] rounded-xl overflow-hidden" data-testid={`notarization-card-${n.notarization_id}`}>
      <div className="flex items-start gap-3 p-4">
        <div className={`w-8 h-8 rounded-full ${cfg.bg} border ${cfg.border} flex items-center justify-center flex-shrink-0 mt-0.5`}>
          <Icon className={`w-4 h-4 ${cfg.color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-white text-sm font-medium truncate">{n.document_name}</p>
            <span className={`text-[10px] px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.color} border ${cfg.border} font-semibold uppercase tracking-wider flex-shrink-0`}>
              {cfg.label}
            </span>
          </div>
          <p className="text-white/30 text-xs mt-0.5">
            Requested by {n.requestor_name} · {relTime(n.requested_at)}
          </p>
          {n.status === "notarized" && n.certificate_id && (
            <div className="flex items-center gap-2 mt-1.5">
              <span className="text-[10px] font-mono text-[#D4AF37] bg-[#D4AF37]/10 border border-[#D4AF37]/20 px-2 py-0.5 rounded">
                {n.certificate_id}
              </span>
              <button
                onClick={() => { navigator.clipboard.writeText(n.certificate_id); toast.success("Cert ID copied!"); }}
                className="text-white/20 hover:text-white/50 transition-colors"
              >
                <Copy className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>
        <button onClick={() => setExpanded(e => !e)} className="text-white/20 hover:text-white/50 flex-shrink-0">
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-white/[0.05] pt-3">
          {n.parties?.length > 0 && (
            <div>
              <p className="text-[10px] text-white/30 uppercase tracking-wider mb-2">Parties</p>
              <div className="flex flex-wrap gap-1.5">
                {n.parties.map((p, i) => (
                  <span key={i} className="text-[10px] px-2 py-0.5 bg-white/[0.04] border border-white/[0.06] rounded text-white/50 capitalize">
                    {p.name} · {p.role}
                  </span>
                ))}
              </div>
            </div>
          )}
          {n.notary_name && (
            <div>
              <p className="text-[10px] text-white/30 uppercase tracking-wider mb-1">Notarized By</p>
              <p className="text-white/70 text-xs">{n.notary_name} · {relTime(n.notarized_at)}</p>
            </div>
          )}
          {n.notary_statement && (
            <div>
              <p className="text-[10px] text-white/30 uppercase tracking-wider mb-1">Notary Statement</p>
              <p className="text-white/50 text-xs leading-relaxed italic">"{n.notary_statement}"</p>
            </div>
          )}
          {n.notes && (
            <div>
              <p className="text-[10px] text-white/30 uppercase tracking-wider mb-1">Notes</p>
              <p className="text-white/50 text-xs">{n.notes}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Panel ─────────────────────────────────────────────────────────────────
export default function NotarizationPanel({ deal }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showRequest, setShowRequest] = useState(false);
  const [completeTarget, setCompleteTarget] = useState(null);

  const fetchRecords = async () => {
    if (!deal?.deal_id) return;
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/notarize/deal/${deal.deal_id}`, { withCredentials: true });
      setRecords(res.data?.notarizations || []);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { fetchRecords(); }, [deal?.deal_id]);

  const pending = records.filter(r => r.status === "pending");
  const notarized = records.filter(r => r.status === "notarized");

  return (
    <div className="p-5 space-y-4" data-testid="notarization-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] text-white/30 uppercase tracking-wider">Electronic Notarization</p>
          <p className="text-white/40 text-xs mt-0.5">{notarized.length} notarized · {pending.length} pending</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchRecords} disabled={loading} className="p-1.5 text-white/20 hover:text-white/50 transition-all rounded-lg">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
          <button
            onClick={() => setShowRequest(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#D4AF37]/10 border border-[#D4AF37]/20 text-[#D4AF37] text-xs font-semibold rounded-lg hover:bg-[#D4AF37]/20 transition-all"
            data-testid="request-notarization-btn"
          >
            <PlusCircle className="w-3.5 h-3.5" />
            Request
          </button>
        </div>
      </div>

      {/* Empty State */}
      {records.length === 0 && !loading && (
        <div className="text-center py-14">
          <div className="w-12 h-12 rounded-2xl bg-white/[0.03] border border-white/[0.06] flex items-center justify-center mx-auto mb-3">
            <FileCheck className="w-5 h-5 text-white/15" />
          </div>
          <p className="text-white/30 text-sm">No notarizations yet</p>
          <p className="text-white/15 text-xs mt-1">Request notarization for any deal document</p>
          <button
            onClick={() => setShowRequest(true)}
            className="mt-4 px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/20 text-[#D4AF37] text-xs font-semibold rounded-lg hover:bg-[#D4AF37]/20 transition-all"
          >
            Request First Notarization
          </button>
        </div>
      )}

      {/* Pending */}
      {pending.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] text-white/20 uppercase tracking-wider">Awaiting Notarization</p>
          {pending.map(n => (
            <div key={n.notarization_id} className="space-y-2">
              <CertificateCard n={n} />
              <button
                onClick={() => setCompleteTarget(n)}
                className="w-full py-2 bg-emerald-500/10 border border-emerald-500/20 text-[#00FF88] text-xs font-semibold rounded-lg hover:bg-emerald-500/20 transition-all"
                data-testid={`notarize-action-btn-${n.notarization_id}`}
              >
                Notarize This Document
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Completed */}
      {notarized.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] text-white/20 uppercase tracking-wider">Completed</p>
          {notarized.map(n => <CertificateCard key={n.notarization_id} n={n} />)}
        </div>
      )}

      {/* Modals */}
      {showRequest && (
        <RequestModal
          dealId={deal?.deal_id}
          onClose={() => setShowRequest(false)}
          onCreated={rec => setRecords(r => [rec, ...r])}
        />
      )}
      {completeTarget && (
        <CompleteModal
          notarization={completeTarget}
          onClose={() => setCompleteTarget(null)}
          onCompleted={updated => setRecords(r => r.map(x => x.notarization_id === updated.notarization_id ? updated : x))}
        />
      )}
    </div>
  );
}
